import { useState, useEffect } from 'react';
import type { Theme } from '../types';

interface CompactRelationshipTimerProps {
  theme: Theme;
  officialDate: Date;
  unofficialDate?: Date;
}

interface TimeUnits {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalDays: number;
}

const CompactRelationshipTimer = ({ theme, officialDate, unofficialDate }: CompactRelationshipTimerProps) => {
  const [time, setTime] = useState<TimeUnits | null>(null);
  const [showMode, setShowMode] = useState<'official' | 'unofficial'>('official');

  const calculateTimeDifference = (startDate: Date): TimeUnits => {
    const now = new Date();
    const diff = now.getTime() - startDate.getTime();

    // Calculate total days
    const totalDays = Math.floor(diff / (1000 * 60 * 60 * 24));

    // Calculate years, months, days
    let years = now.getFullYear() - startDate.getFullYear();
    let months = now.getMonth() - startDate.getMonth();
    let days = now.getDate() - startDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    // Calculate time units
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return {
      years,
      months,
      days,
      hours,
      minutes,
      seconds,
      totalDays
    };
  };

  useEffect(() => {
    const updateTimer = () => {
      const activeDate = showMode === 'official' ? officialDate : unofficialDate;
      if (activeDate) {
        setTime(calculateTimeDifference(activeDate));
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [officialDate, unofficialDate, showMode]);

  if (!time) return null;

  const currentDate = showMode === 'official' ? officialDate : unofficialDate;

  return (
    <div className={`${theme.secondary} rounded-2xl p-6 shadow-lg`}>
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{showMode === 'official' ? '💑' : '✨'}</span>
          <h3 className={`text-lg font-bold ${theme.text}`}>
            {showMode === 'official' ? 'Together' : 'Since the Beginning'}
          </h3>
        </div>
        {unofficialDate && (
          <button
            onClick={() => setShowMode(showMode === 'official' ? 'unofficial' : 'official')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              showMode === 'official'
                ? 'bg-white text-gray-700 hover:bg-gray-50'
                : 'bg-white/50 text-gray-600 hover:bg-white/70'
            }`}
          >
            {showMode === 'official' ? 'Show Unofficial' : 'Show Official'}
          </button>
        )}
      </div>

      {/* Main counter - Horizontal layout */}
      <div className="grid grid-cols-6 gap-2 mb-4">
        {/* Years */}
        {time.years > 0 && (
          <div className="bg-white rounded-lg p-3 text-center">
            <div className={`text-3xl font-bold ${theme.accent}`}>
              {time.years}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {time.years === 1 ? 'Year' : 'Years'}
            </div>
          </div>
        )}

        {/* Months */}
        <div className="bg-white rounded-lg p-3 text-center">
          <div className={`text-3xl font-bold ${theme.accent}`}>
            {time.months}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {time.months === 1 ? 'Month' : 'Months'}
          </div>
        </div>

        {/* Days */}
        <div className="bg-white rounded-lg p-3 text-center">
          <div className={`text-3xl font-bold ${theme.accent}`}>
            {time.days}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {time.days === 1 ? 'Day' : 'Days'}
          </div>
        </div>

        {/* Hours */}
        <div className="bg-white rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${theme.text}`}>
            {String(time.hours).padStart(2, '0')}
          </div>
          <div className="text-xs text-gray-500 mt-1">Hrs</div>
        </div>

        {/* Minutes */}
        <div className="bg-white rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${theme.text}`}>
            {String(time.minutes).padStart(2, '0')}
          </div>
          <div className="text-xs text-gray-500 mt-1">Min</div>
        </div>

        {/* Seconds */}
        <div className="bg-white rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${theme.text}`}>
            {String(time.seconds).padStart(2, '0')}
          </div>
          <div className="text-xs text-gray-500 mt-1">Sec</div>
        </div>
      </div>

      {/* Summary text */}
      <div className={`text-center text-sm ${theme.text} opacity-70 space-y-1`}>
        <div>
          {time.years > 0 && `${time.years} ${time.years === 1 ? 'year' : 'years'}, `}
          {time.months} {time.months === 1 ? 'month' : 'months'}, and {time.days} {time.days === 1 ? 'day' : 'days'}
        </div>
        <div className="text-xs opacity-60">
          {time.totalDays.toLocaleString()} total days since{' '}
          {currentDate?.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
      </div>
    </div>
  );
};

export default CompactRelationshipTimer;